import { ShootSummaryInput } from './shootSummary';
import { messageText } from './messageTemplateCache';

export type NotifyChannel = 'email' | 'whatsapp' | 'both' | 'skip';

export type ClientPreference = 'email' | 'whatsapp' | 'either' | string | undefined;

/** Only use preference when it is an explicit verified channel — never assume WhatsApp from a phone alone. */
export function defaultNotifyChannel(preference?: ClientPreference, options?: {
  emailQueuedAlready?: boolean;
  hasEmail?: boolean;
  hasPhone?: boolean;
}): NotifyChannel {
  if (options?.emailQueuedAlready) {
    return options.hasPhone ? 'whatsapp' : 'skip';
  }
  const pref = String(preference || '').toLowerCase();
  if (pref === 'email' && options?.hasEmail !== false) return 'email';
  if (pref === 'whatsapp' && options?.hasPhone) return 'whatsapp';
  if (pref === 'either' || pref === 'both') {
    if (options?.hasEmail !== false && options?.hasPhone) return 'both';
    if (options?.hasPhone) return 'whatsapp';
    return 'email';
  }
  return 'email';
}

export function describeEmailStatus(status?: string, destination?: string, deliveryConfirmed?: boolean) {
  const where = destination ? ` for ${destination}` : '';
  if (deliveryConfirmed === true) return `Email delivery confirmed${where}.`;
  if (status === 'sent') return `Accepted by the email provider${where}. Delivery is not confirmed.`;
  if (status === 'queued') return 'Email queued.';
  if (status === 'captured') return `Email stored locally${where} and not sent.`;
  if (status === 'not_configured') return 'Email not configured. Message saved in the studio only.';
  if (status === 'failed') return 'Email failed. The message remains saved.';
  if (status === 'preview') return 'Message saved (preview).';
  if (status === 'delivered') return `Email delivery confirmed${where}.`;
  return status ? `Email status: ${status}.` : '';
}

/** Only an email the provider accepted counts as the client having been told. */
export function clientWasEmailed(delivery?: string, providerAccepted?: boolean) {
  return delivery === 'sent' && providerAccepted === true;
}

export function whyClientWasNotEmailed(delivery?: string, destination?: string) {
  if (!destination) return 'There is no email address for this client.';
  if (delivery === 'captured') return 'Email is in test mode, so nothing was sent.';
  if (delivery === 'not_configured') return 'Email sending is not set up yet.';
  if (delivery === 'failed') return `The email to ${destination} did not go through.`;
  return 'The email has not gone out.';
}

export function replyWhatsAppText(input: { clientName?: string; text: string; link?: string; booking?: boolean }) {
  const first = String(input.clientName || '').trim().split(/\s+/)[0];
  const opener = first
    ? messageText('shared.whatsapp-opener-named', 'body', 'Hi {firstName}, it\'s Carmen from Carmen Lorraine Photography.', { firstName: first })
    : messageText('shared.whatsapp-opener-plain', 'body', 'Hi, it\'s Carmen from Carmen Lorraine Photography.');
  const pageLine = input.link
    ? messageText(
      input.booking ? 'shared.page-line-booking' : 'shared.page-line-quotation',
      'body',
      input.booking ? 'Your booking:\n{link}' : 'Your quotation:\n{link}',
      { link: input.link },
    )
    : '';
  const page = pageLine ? `\n\n${pageLine}` : '';
  return messageText('studio-reply', 'whatsapp', '{openerNamed}\n\n{replyText}{page}', {
    openerNamed: opener,
    replyText: input.text.trim(),
    page,
  });
}

export function buildStudioWhatsAppUrl(phoneE164: string | undefined, message: string) {
  const phone = String(phoneE164 || '').replace(/\D/g, '');
  if (phone.length < 8 || phone.length > 15) return '';
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export function openWhatsAppHandoff(url: string) {
  if (!url) return { opened: false as const, message: 'No WhatsApp number on this client.' };
  window.open(url, '_blank', 'noopener,noreferrer');
  return { opened: true as const, message: 'WhatsApp opened — confirm sending in WhatsApp.' };
}

export function bookingUpdateWhatsAppText(input: {
  quoteNumber?: string;
  shootDate?: string;
  startTime?: string;
  locationName?: string;
  address?: string;
  mapsUrl?: string;
  durationMinutes?: number | null;
  link?: string;
  kind: 'confirmed' | 'updated';
  sessions?: Array<{ label?: string; shootDate?: string; startTime?: string; locationName?: string; address?: string }>;
}) {
  const reference = input.quoteNumber || 'your booking';
  const sessions = Array.isArray(input.sessions) ? input.sessions.filter(Boolean) : [];
  const schedule = sessions.length > 1
    ? sessions.map((session, index) => {
        const label = String(session.label || '').trim() || `Day ${index + 1}`;
        const when = [session.shootDate, session.startTime].filter(Boolean).join(' at ');
        const place = [session.locationName, session.address].filter(Boolean).join(', ');
        return [label, when, place].filter(Boolean).join(' · ');
      }).join('; ')
    : '';
  const when = schedule || [input.shootDate, input.startTime].filter(Boolean).join(' at ');
  const placeName = schedule ? '' : [input.locationName, input.address].filter(Boolean).join(', ');
  const opener = messageText('shared.whatsapp-opener', 'body', 'Hi, this is Carmen from Carmen Lorraine Photography.');
  const pageLine = input.link
    ? messageText('shared.page-line-booking', 'body', 'Your booking:\n{link}', { link: input.link })
    : '';
  const page = pageLine ? `\n\n${pageLine}` : '';
  if (input.kind === 'confirmed') {
    const pin = input.mapsUrl
      || (placeName ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeName)}` : '');
    const map = pin ? `\n\nMap: ${pin}` : '';
    const where = schedule
      ? ` for ${schedule}`
      : [when ? `for ${when}` : '', placeName ? `at ${placeName}` : ''].filter(Boolean).join(' ');
    const whenPlace = where ? (schedule ? where : ` ${where}`) : '';
    return messageText(
      'shoot-confirmed',
      'whatsapp',
      '{opener} This is the confirmation of your booking {quoteNumber}{whenPlace}.{map}{page}',
      { opener, quoteNumber: reference, whenPlace, map, page },
    );
  }
  return messageText(
    'booking-updated',
    'whatsapp',
    '{opener} This is an amendment to your booking {quoteNumber}.{page}',
    { opener, quoteNumber: reference, page },
  );
}

function reminderTemplateId(label: string, noticeType: string) {
  const key = noticeType || label;
  if (['quotation-follow-up', 'unanswered-quote', 'Quotation unanswered'].includes(key)) return 'quotation-follow-up';
  if (['deposit-reminder', 'deposit-awaiting', 'Deposit awaiting payment'].includes(key)) return 'deposit-reminder';
  if (['shoot-reminder', 'shoot-approaching', 'Confirmed shoot within 48 hours'].includes(key)) return 'shoot-reminder';
  return '';
}

export function reminderWhatsAppText(label: string, quoteNumber?: string, options?: { link?: string; confirmed?: boolean; noticeType?: string } | ShootSummaryInput) {
  const noticeType = options && 'noticeType' in options ? String(options.noticeType || '') : '';
  const booking = noticeType === 'shoot-reminder'
    || noticeType === 'shoot-approaching'
    || label === 'shoot-approaching'
    || label === 'Confirmed shoot within 48 hours'
    || Boolean(options && 'confirmed' in options && options.confirmed);
  const reference = quoteNumber || (booking ? 'your booking' : 'your quotation');
  const opener = messageText('shared.whatsapp-opener', 'body', 'Hi, this is Carmen from Carmen Lorraine Photography.');
  const link = options && 'link' in options ? options.link : '';
  const pageLine = link
    ? messageText(
      booking ? 'shared.page-line-booking' : 'shared.page-line-quotation',
      'body',
      booking ? 'Your booking:\n{link}' : 'Your quotation:\n{link}',
      { link },
    )
    : '';
  const page = pageLine ? `\n\n${pageLine}` : '';
  const id = reminderTemplateId(label, noticeType);
  const field = booking && id === 'deposit-reminder' ? 'whatsappBooking' : 'whatsapp';
  const fallbacks: Record<string, string> = {
    'quotation-follow-up': '{opener} This is a reminder about your quotation {quoteNumber}.{page}',
    'deposit-reminder': booking
      ? '{opener} This is a reminder that the deposit for your booking {quoteNumber} is still due.{page}'
      : '{opener} This is a reminder that the deposit for quotation {quoteNumber} is still due.{page}',
    'shoot-reminder': '{opener} This is a reminder of your booking {quoteNumber}.{page}',
  };
  if (id) {
    return messageText(id, field, fallbacks[id], { opener, quoteNumber: reference, page });
  }
  return `${opener} This is a reminder about ${reference}.${page}`;
}
